import { supabase } from '../lib/supabase';
import { ArtCategory } from '../types';
import { assertContentIsAllowed } from '../lib/contentModeration';

interface EventOrganizer {
  id: string;
  username?: string;
  full_name?: string;
  avatar_url?: string;
  is_premium?: boolean;
}

interface EventAttendee {
  id?: string;
  user_id: string;
  status: string;
}

export interface Event {
  id: string;
  organizer_id: string;
  title: string;
  description: string;
  category: ArtCategory;
  event_type: 'exhibition' | 'workshop' | 'concert' | 'meetup' | 'online';
  start_date: string;
  end_date: string;
  location?: string;
  virtual_link?: string;
  price: number;
  max_attendees?: number;
  image_url?: string;
  tags: string[];
  status: 'draft' | 'published' | 'cancelled' | 'completed';
  created_at: string;
  updated_at: string;
  organizer?: EventOrganizer;
  attendees_count?: number;
  is_registered?: boolean;
}

export const eventsService = {
  async getEvents(options?: {
    category?: ArtCategory | 'all';
    eventType?: string | 'all';
    location?: string;
    status?: string | 'all';
    upcoming?: boolean;
    limit?: number;
    offset?: number;
  }) {
    let query = supabase
      .from('events')
      .select(`
        *,
        profiles!events_organizer_id_fkey (
          id,
          username,
          full_name,
          avatar_url,
          is_premium
        ),
        event_attendees (
          id,
          user_id,
          status
        )
      `)
      .eq('status', 'published');

    // Filters
    if (options?.category && options.category !== 'all') {
      query = query.eq('category', options.category);
    }
    
    if (options?.eventType && options.eventType !== 'all') {
      query = query.eq('event_type', options.eventType);
    }
    
    if (options?.location) {
      query = query.ilike('location', `%${options.location}%`);
    }

    if (options?.upcoming) {
      query = query.gte('start_date', new Date().toISOString());
    }

    // Sort by start date
    query = query.order('start_date', { ascending: true });

    // Pagination
    if (options?.limit) {
      query = query.limit(options.limit);
    }
    if (options?.offset) {
      query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
    }

    const { data, error } = await query;
    if (error) throw error;

    // Process data to add attendees count and registration status
    const { data: { user } } = await supabase.auth.getUser();
    
    return data?.map(event => ({
      ...event,
      organizer: (event as Record<string, unknown>).profiles as EventOrganizer,
      attendees_count: (event.event_attendees as EventAttendee[] | undefined)?.filter((a) => a.status === 'registered' || a.status === 'confirmed').length || 0,
      is_registered: user ? (event.event_attendees as EventAttendee[] | undefined)?.some((a) => a.user_id === user.id && (a.status === 'registered' || a.status === 'confirmed')) : false
    })) || [];
  },

  async getMyEvents() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('events')
      .select(`
        *,
        event_attendees (
          id,
          user_id,
          status,
          registered_at,
          profiles!event_attendees_user_id_fkey (
            username,
            full_name,
            avatar_url
          )
        )
      `)
      .eq('organizer_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data?.map(event => ({
      ...event,
      attendees_count: (event.event_attendees as EventAttendee[] | undefined)?.filter((a) => a.status === 'registered' || a.status === 'confirmed').length || 0
    })) || [];
  },

  async getMyRegistrations() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('event_attendees')
      .select(`
        *,
        events (
          *,
          profiles!events_organizer_id_fkey (
            username,
            full_name,
            avatar_url
          )
        )
      `)
      .eq('user_id', user.id)
      .in('status', ['registered', 'confirmed'])
      .order('registered_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async createEvent(eventData: {
    title: string;
    description: string;
    category: ArtCategory;
    event_type: 'exhibition' | 'workshop' | 'concert' | 'meetup' | 'online';
    start_date: string;
    end_date: string;
    location?: string;
    virtual_link?: string;
    price?: number;
    max_attendees?: number;
    image_url?: string;
    tags?: string[];
    status?: 'draft' | 'published';
    external_url?: string;
    is_external?: boolean;
    banner_url?: string;
  }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    await assertContentIsAllowed([
      eventData.title,
      eventData.description,
      eventData.location,
      eventData.virtual_link,
      Array.isArray(eventData.tags) ? eventData.tags.join(' ') : undefined,
    ]);

    const { data, error } = await supabase
      .from('events')
      .insert({
        organizer_id: user.id,
        title: eventData.title,
        description: eventData.description,
        category: eventData.category,
        event_type: eventData.event_type,
        start_date: eventData.start_date,
        end_date: eventData.end_date,
        location: eventData.location || null,
        virtual_link: eventData.virtual_link || null,
        external_url: eventData.external_url || null,
        is_external: eventData.is_external || false,
        price: eventData.price || 0,
        max_attendees: eventData.max_attendees || null,
        image_url: eventData.image_url || null,
        banner_url: eventData.banner_url || null,
        tags: eventData.tags || [],
        status: eventData.status || 'draft',
      })
      .select(`
        *,
        profiles!events_organizer_id_fkey (
          id,
          username,
          full_name,
          avatar_url,
          is_premium
        )
      `)
      .single();

    if (error) throw error;
    return data;
  },

  async registerForEvent(eventId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('event_attendees')
      .insert({
        event_id: eventId,
        user_id: user.id,
        status: 'registered',
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async unregisterFromEvent(eventId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('event_attendees')
      .delete()
      .eq('event_id', eventId)
      .eq('user_id', user.id);

    if (error) throw error;
  },

  async updateEventRegistrationStatus(eventId: string, status: 'registered' | 'confirmed' | 'cancelled') {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('event_attendees')
      .update({ status })
      .eq('event_id', eventId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateEventStatus(eventId: string, status: 'draft' | 'published' | 'cancelled' | 'completed') {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('events')
      .update({ 
        status,
        updated_at: new Date().toISOString()
      })
      .eq('id', eventId)
      .eq('organizer_id', user.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },
};
